import type {
  AutomationExecutionParams,
  AutomationExecution,
  AutomationHistory,
  AutomationHistoryParams,
  AutomationExecutionVariables,
  GetAutomationMessageResponse,
  RunToolsAsOptions,
  RunToolsAsOptionsParams,
} from '@kizen/api/automation';
import { CursorPaginationResponse } from '@kizen/api';
import type {
  DefaultError,
  InfiniteData,
  InfiniteQueryObserverOptions,
  QueryObserverOptions,
} from '@tanstack/query-core';
import type { AxiosInstance } from 'axios';
import type { Resource } from './resource';
import type {
  Drop,
  WithInfiniteSelect,
  WithParams,
  WithRequired,
} from './types';

import {
  getAutomationHistory,
  getAutomationExecutionVariables,
  getAutomationMessage,
  getAutomationExecution,
  getAutomationExecutionById,
  getRunToolsAsOptions,
} from '@kizen/api/automation';
import { getResource } from './resource';
import { curry1 } from './utils';

type AutomationHistoryQuery<TData = AutomationHistory> = WithRequired<
  QueryObserverOptions<
    AutomationHistory,
    DefaultError,
    TData,
    AutomationHistory,
    [
      Resource<
        Readonly<
          ['automation2', 'automations', string, 'modification-history']
        >,
        AutomationHistoryParams
      >,
    ]
  >,
  'queryFn' | 'queryKey'
>;

export const automationHistoryQuery = <TData = AutomationHistory>(
  axios: AxiosInstance,
  id: string,
  options: WithParams<
    Omit<AutomationHistoryQuery<TData>, 'queryFn' | 'queryKey'>,
    AutomationHistoryParams
  > = {}
): AutomationHistoryQuery<TData> => {
  const path = [
    'automation2',
    'automations',
    id,
    'modification-history',
  ] as const;
  const { params, ...queryOpts } = options;
  const resource = getResource(path, { params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getAutomationHistory(axios, qk.path[2], {
        params: qk.params,
        signal,
      });
    },
    ...queryOpts,
  };
};

type AutomationExecutionVariablesQuery<TData = AutomationExecutionVariables> =
  WithRequired<
    QueryObserverOptions<
      AutomationExecutionVariables,
      DefaultError,
      TData,
      AutomationExecutionVariables,
      [
        Resource<
          Readonly<['automation2', 'automation-execution', string, 'variables']>
        >,
      ]
    >,
    'queryFn' | 'queryKey'
  >;

export const automationExecutionVariablesQuery = <
  TData = AutomationExecutionVariables,
>(
  axios: AxiosInstance,
  id: string,
  options: Omit<
    AutomationExecutionVariablesQuery<TData>,
    'queryFn' | 'queryKey'
  > = {}
): AutomationExecutionVariablesQuery<TData> => {
  const path = [
    'automation2',
    'automation-execution',
    id,
    'variables',
  ] as const;
  const resource = getResource(path);

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getAutomationExecutionVariables(axios, qk.path[2], {
        signal,
      });
    },
    ...options,
  };
};

type AutomationMessageQuery<
  TData = GetAutomationMessageResponse,
  TError = DefaultError,
> = WithRequired<
  QueryObserverOptions<
    GetAutomationMessageResponse,
    TError,
    TData,
    GetAutomationMessageResponse,
    [Resource<Readonly<['messages', 'automations', string]>>]
  >,
  'queryFn' | 'queryKey'
>;

export const automationMessageQuery = <TData = GetAutomationMessageResponse>(
  axios: AxiosInstance,
  id: string,
  options: Omit<AutomationMessageQuery<TData>, 'queryFn' | 'queryKey'> = {}
): AutomationMessageQuery<TData> => {
  const path = ['messages', 'automations', id] as const;
  const resource = getResource(path);

  return <AutomationMessageQuery<TData>>{
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getAutomationMessage(axios, qk.path[2], {
        signal,
      });
    },
    ...options,
  };
};

type AutomationExecutionInfiniteQuery<
  TData = InfiniteData<CursorPaginationResponse<AutomationExecution>>,
  TError = DefaultError,
> = WithRequired<
  WithInfiniteSelect<
    InfiniteQueryObserverOptions<
      CursorPaginationResponse<AutomationExecution>,
      TError,
      TData,
      CursorPaginationResponse<AutomationExecution>,
      [
        Resource<
          Readonly<['automation2', 'automation-execution']>,
          AutomationExecutionParams
        >,
      ],
      string | undefined
    >
  >,
  'queryFn' | 'queryKey'
>;

export type AutomationExecutionInfiniteQueryOptions<TData> = WithParams<
  Omit<
    AutomationExecutionInfiniteQuery<TData>,
    | 'queryFn'
    | 'queryKey'
    | 'getNextPageParam'
    | 'getPreviousPageParam'
    | 'initialPageParam'
  >,
  AutomationExecutionParams
>;

export const automationExecutionInfiniteQuery = <
  TData = InfiniteData<CursorPaginationResponse<AutomationExecution>>,
>(
  axios: AxiosInstance,
  options: AutomationExecutionInfiniteQueryOptions<TData> = {}
): AutomationExecutionInfiniteQuery<TData> => {
  const path = ['automation2', 'automation-execution'] as const;
  const { params, ...queryOpts } = options;
  const resource = getResource(path, { params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getAutomationExecution(axios, {
        params: {
          ...qk.params,
          cursor: context.pageParam ?? qk.params?.cursor,
        },
        signal,
      });
    },
    getNextPageParam: (lastPage) => lastPage.next_cursor,
    getPreviousPageParam: (previousPage) => previousPage.prev_cursor,
    initialPageParam: undefined,
    ...queryOpts,
  };
};

type AutomationExecutionByIdQuery<
  TData = AutomationExecution,
  TError = DefaultError,
> = WithRequired<
  QueryObserverOptions<
    AutomationExecution,
    TError,
    TData,
    AutomationExecution,
    [Resource<Readonly<['automation2', 'automation-execution', string]>>]
  >,
  'queryFn' | 'queryKey'
>;

export const automationExecutionByIdQuery = <TData = AutomationExecution>(
  axios: AxiosInstance,
  id: string,
  options: Omit<
    AutomationExecutionByIdQuery<TData>,
    'queryFn' | 'queryKey'
  > = {}
): AutomationExecutionByIdQuery<TData> => {
  const path = ['automation2', 'automation-execution', id] as const;
  const resource = getResource(path);

  return <AutomationExecutionByIdQuery<TData>>{
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
      } = context;

      return getAutomationExecutionById(axios, qk.path[2]);
    },
    ...options,
  };
};

type RunToolsAsOptionsQuery<TData = RunToolsAsOptions> = WithRequired<
  QueryObserverOptions<
    RunToolsAsOptions,
    DefaultError,
    TData,
    RunToolsAsOptions,
    [
      Resource<
        Readonly<['automation2', 'automations', 'run-tools-as-options']>,
        RunToolsAsOptionsParams
      >,
    ]
  >,
  'queryFn' | 'queryKey'
>;

export const runToolsAsOptionsQuery = <TData = RunToolsAsOptions>(
  axios: AxiosInstance,
  options: WithParams<
    Omit<RunToolsAsOptionsQuery<TData>, 'queryFn' | 'queryKey'>,
    RunToolsAsOptionsParams
  > = {}
): RunToolsAsOptionsQuery<TData> => {
  const path = ['automation2', 'automations', 'run-tools-as-options'] as const;
  const { params, ...queryOpts } = options;
  const resource = getResource(path, { params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getRunToolsAsOptions(axios, {
        params: qk.params,
        signal,
      });
    },
    ...queryOpts,
  };
};

class Queries {
  public automationHistoryQuery: <TData = AutomationHistory>(
    ...args: Drop<Parameters<typeof automationHistoryQuery<TData>>>
  ) => AutomationHistoryQuery<TData>;

  public automationExecutionVariablesQuery: <
    TData = AutomationExecutionVariables,
  >(
    ...args: Drop<Parameters<typeof automationExecutionVariablesQuery<TData>>>
  ) => AutomationExecutionVariablesQuery<TData>;

  public automationExecutionInfiniteQuery: <
    TData = InfiniteData<CursorPaginationResponse<AutomationExecution>>,
  >(
    ...args: Drop<Parameters<typeof automationExecutionInfiniteQuery<TData>>>
  ) => AutomationExecutionInfiniteQuery<TData>;

  public automationExecutionByIdQuery: <TData = AutomationExecution>(
    ...args: Drop<Parameters<typeof automationExecutionByIdQuery<TData>>>
  ) => AutomationExecutionByIdQuery<TData>;

  public automationMessageQuery: <TData = GetAutomationMessageResponse>(
    ...args: Drop<Parameters<typeof automationMessageQuery<TData>>>
  ) => AutomationMessageQuery<TData>;

  public runToolsAsOptionsQuery: <TData = RunToolsAsOptions>(
    ...args: Drop<Parameters<typeof runToolsAsOptionsQuery<TData>>>
  ) => RunToolsAsOptionsQuery<TData>;

  constructor(axios: AxiosInstance) {
    this.automationHistoryQuery = curry1(automationHistoryQuery)(axios);
    this.automationExecutionInfiniteQuery = curry1(
      automationExecutionInfiniteQuery
    )(axios);
    this.automationExecutionVariablesQuery = curry1(
      automationExecutionVariablesQuery
    )(axios);
    this.automationExecutionByIdQuery = curry1(automationExecutionByIdQuery)(
      axios
    );
    this.automationMessageQuery = curry1(automationMessageQuery)(axios);
    this.runToolsAsOptionsQuery = curry1(runToolsAsOptionsQuery)(axios);
  }
}

export type AutomationQueries = Queries;

export const defAutomationQueries = (
  axios: AxiosInstance
): AutomationQueries => {
  return new Queries(axios);
};
